import type { AuswertungAntwort, AuswertungGeraet, AuswertungKontext, PushArt } from '../../shared/api.js';
import { PRUEFPUNKTE } from '../../shared/pruefpunkte.js';
import { api, ApiFehler, authKopf, fehlerText } from '../api.js';
import { ersetze, h, haengeAn, knopf, meldung, setzeMeldung, statusMarke } from '../dom.js';
import type { Ansicht } from '../router.js';
import { istAngemeldet } from '../sitzung.js';
import { formatiereZeitpunkt, vorWieLange } from '../zeit.js';
import { Aufraeumer } from './gemeinsam.js';
import { hatZugang, zugangAbgelehnt, zugangsFormular } from './zugang.js';

function kontextText(k: AuswertungKontext): string {
  const p = k.plattform;
  return `${k.modus === 'app' ? 'App' : 'Browser'} · ${p.geraet ? `${p.geraet}, ` : ''}${p.system} · ${p.browser}`;
}

export const auswertung: Ansicht = async (ziel) => {
  if (!(await hatZugang())) {
    zugangsFormular(ziel, 'Auswertung aller Geräte');
    return;
  }
  const alsGeraet = await istAngemeldet();
  const aufraeumer = new Aufraeumer();
  const inhalt = h('div', {});
  const status = meldung();

  async function sendePush(geraetId: string, art: PushArt): Promise<void> {
    try {
      await api('/api/push/senden', { json: { verzoegerungSek: 0, art, geraetId }, auswertung: true });
      setzeMeldung(status, art === 'neustart' ? 'Neustart-Test-Push gesendet.' : 'Test-Push gesendet.', 'ok');
      await lade();
    } catch (fehler) {
      if (await zugangAbgelehnt(fehler)) return;
      setzeMeldung(status, fehlerText(fehler), 'fehler');
    }
  }

  async function ladeCsv(): Promise<void> {
    const antwort = await fetch('/api/auswertung/ergebnisse.csv', { headers: await authKopf(), cache: 'no-store' });
    if (!antwort.ok) {
      if (await zugangAbgelehnt(new ApiFehler(antwort.status, 'Export abgelehnt.'))) return;
      setzeMeldung(status, `Export fehlgeschlagen (${antwort.status}). Bitte erneut versuchen.`, 'fehler');
      return;
    }
    const url = URL.createObjectURL(await antwort.blob());
    const link = h('a', { href: url, download: `tfm-geraetetest-${new Date().toISOString().slice(0, 10)}.csv` });
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  function geraetKarte(g: AuswertungGeraet): HTMLElement {
    const klassen = new Set(g.kontexte.map((k) => k.klasse));
    const punkte = PRUEFPUNKTE.filter((p) => p.klassen.some((k) => klassen.has(k)) || g.ergebnisse.some((e) => e.pruefpunkt === p.key));
    return h(
      'section',
      { class: 'karte geraet' },
      h('h2', {}, g.name),
      h(
        'ul',
        { class: 'kontexte' },
        ...g.kontexte.map((k) =>
          h(
            'li',
            {},
            h('span', {}, kontextText(k)),
            h('span', { class: 'leise' }, ` · zuletzt ${vorWieLange(k.zuletztGesehen)}`),
            k.pushAbo &&
              h(
                'span',
                { class: 'knopfreihe klein' },
                knopf('Test-Push', () => sendePush(k.id, 'manuell'), 'sekundaer'),
                knopf('Neustart-Push', () => sendePush(k.id, 'neustart'), 'sekundaer'),
              ),
          ),
        ),
      ),
      h(
        'div',
        { class: 'tabelle-rahmen' },
        h(
          'table',
          { class: 'tabelle' },
          h('thead', {}, h('tr', {}, h('th', { scope: 'col' }, 'Prüfpunkt'), h('th', { scope: 'col' }, 'Ergebnis'), h('th', { scope: 'col' }, 'Details'))),
          h(
            'tbody',
            {},
            ...punkte.map((p) => {
              const e = g.ergebnisse.find((x) => x.pruefpunkt === p.key);
              // Zählt der Prüfpunkt in App und Browser getrennt, steht das Ergebnis aus dem Browser darunter.
              const b = g.imBrowser.find((x) => x.pruefpunkt === p.key);
              return h(
                'tr',
                {},
                h('th', { scope: 'row' }, p.titel),
                h('td', {}, statusMarke(e?.status ?? 'offen'), b && h('span', { class: 'zweitmarke' }, statusMarke(b.status))),
                h(
                  'td',
                  {},
                  e ? `${e.text} (${formatiereZeitpunkt(e.erfasstAm)}${e.modus === 'browser' ? ', Browser' : b ? ', App' : ''})` : '–',
                  b && h('span', { class: 'zweitzeile' }, `Im Browser: ${b.text} (${formatiereZeitpunkt(b.erfasstAm)})`),
                ),
              );
            }),
          ),
        ),
      ),
    );
  }

  async function lade(): Promise<void> {
    let daten: AuswertungAntwort;
    try {
      daten = await api<AuswertungAntwort>('/api/auswertung', { auswertung: true });
    } catch (fehler) {
      if (await zugangAbgelehnt(fehler)) return;
      ersetze(inhalt, h('p', { class: 'meldung meldung-fehler' }, fehlerText(fehler)));
      return;
    }
    ersetze(
      inhalt,
      h('p', { class: 'leise' }, `Stand ${formatiereZeitpunkt(daten.serverZeit)} · ${daten.geraete.length} ${daten.geraete.length === 1 ? 'Gerät' : 'Geräte'}`),
      ...daten.geraete.map(geraetKarte),
      h(
        'section',
        { class: 'karte' },
        h('h2', {}, 'QR-Scans'),
        daten.qrScans.length === 0
          ? h('p', { class: 'leise' }, 'Noch keine Scans.')
          : h(
              'div',
              { class: 'tabelle-rahmen' },
              h(
                'table',
                { class: 'tabelle' },
                h('thead', {}, h('tr', {}, ...['Zeit', 'Aufkleber', 'Pfad wie angekommen', 'Unverändert', 'Browser'].map((t) => h('th', { scope: 'col' }, t)))),
                h(
                  'tbody',
                  {},
                  ...daten.qrScans.map((s) =>
                    h(
                      'tr',
                      {},
                      h('td', {}, formatiereZeitpunkt(s.empfangenAm)),
                      h('td', {}, s.variante === 'gross' ? 'A (groß)' : s.variante === 'gemischt' ? 'B (Vergleich)' : 'unbekannt'),
                      h('td', { class: 'code', translate: 'no' }, s.pfadRoh),
                      h('td', {}, s.pfadUnveraendert ? 'ja' : 'nein'),
                      h('td', { class: 'ua' }, s.ua),
                    ),
                  ),
                ),
              ),
            ),
      ),
      h(
        'section',
        { class: 'karte' },
        h('h2', {}, 'Push-Nachrichten'),
        daten.push.length === 0
          ? h('p', { class: 'leise' }, 'Noch keine Nachrichten.')
          : h(
              'ul',
              { class: 'nachrichten' },
              ...daten.push.map((n) =>
                h(
                  'li',
                  {},
                  h('strong', {}, `${n.geraetName}: ${n.art}, fällig ${formatiereZeitpunkt(n.faelligAm)}`),
                  h('span', {}, n.sendeStatus ?? 'wartet auf den Versand'),
                  h('span', {}, n.empfangenAm ? [h('span', { 'aria-hidden': 'true' }, '✓ '), `angekommen ${formatiereZeitpunkt(n.empfangenAm)}`] : 'kein Empfang bestätigt'),
                ),
              ),
            ),
      ),
    );
  }

  haengeAn(ziel, [
    // Die Übersicht gibt es nur für eingerichtete Testgeräte.
    alsGeraet && h('a', { href: '/', class: 'zurueck' }, h('span', { 'aria-hidden': 'true' }, '← '), 'Übersicht'),
    h('h1', {}, 'Auswertung aller Geräte'),
    h('div', { class: 'knopfreihe' }, knopf('Aktualisieren', () => lade(), 'sekundaer'), knopf('Alle Ergebnisse als CSV', () => ladeCsv(), 'sekundaer')),
    status,
    inhalt,
  ]);
  await lade();
  return aufraeumer.alles;
};
